import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import { PageTitle } from '../../layout/core';
import { batchesBreadCrumbs } from '../batches/BatchesListPage';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { FinancialAccountModalForm } from '../../components/financial_accaunt/FinancialAccountModalForm';
import { shallowEqual, useSelector } from 'react-redux';
import { IAuthState } from '../../redux/AuthRedux';
import { RootState } from '../../../setup';
import { ConfirmPasswordModal } from '../../components/financial_accaunt/ConfirmPasswordModal';
import {
  FinancialAccountFilterSection,
  IFinancialAccountProps,
} from '../../components/financial_accaunt/FinancialAccountFilterSection';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { usePaymentContext } from '../../store/payments/payments-context';
import {
  fetchEntityTypes,
  fetchFinancialAccountData,
} from '../../store/payments/actions/financial-account';
import { IPaginationData } from '../../interfaces/funds';
import { useLocation } from 'react-router';
import { parseSearchParams } from '../../utils/utilities';

export const FinancialAccount: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const userData = authUser.userData;

  const filtersFromUrl: IFinancialAccountProps = parseSearchParams(search) || {};

  const { merchantState: { isPartnerNamesListLoading }} = useMerchants();
  const { dispatch } = usePaymentContext();

  const [relatedEntityType, setRelatedEntityType] = useState<string>('');
  const [financialAccountData, setFinancialAccountData] = useState<IPaginationData>(null);
  const [isFinancialAccountDataLoading, setIsFinancialAccountDataLoading] = useState<boolean>(true);

  const [isLoginVisible, setIsLoginModalVisible] = useState<boolean>(false);


  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });
  const [financialAccountModalState, setFinancialAccountModalState] = useState({
    isOpen: false,
    data: null,
  });

  const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

  const closeFinancialAccountModal = (): void => setFinancialAccountModalState({ isOpen: false, data: null });

  const closeLoginModal = (): void => setIsLoginModalVisible(false);

  const fetchAccountData = async (searchParams: IFinancialAccountProps): Promise<void> => {
    setIsFinancialAccountDataLoading(true);

    let localSearchParams: IFinancialAccountProps;

    if (Object.keys(searchParams).length) {
      localSearchParams = searchParams;

      setRelatedEntityType(searchParams.relatedEntityType ?? '');
    } else {
      localSearchParams = {
        pageSize: '10',
        pageNumber: '1',
      };
    }

    const data = await fetchFinancialAccountData(localSearchParams);
    setFinancialAccountData(data);

    setIsFinancialAccountDataLoading(false);
  };

  useEffect(() => {
    void fetchEntityTypes(dispatch);
  }, []);

  const confirmUpdateFinAcc = async (): Promise<void> => {
    await fetchFinancialAccountData(filtersFromUrl);
    closeFinancialAccountModal();
  };

  const listener = async (searchParams: IFinancialAccountProps): Promise<void> => {
    const offset = 5 * 60 * 1000; // 5 min

    if (userData?.loginDate && (new Date().getTime() - new Date(userData?.loginDate).getTime() > offset )) {
      setIsLoginModalVisible(true);
    } else {
      await fetchAccountData(searchParams);
    }
  };

  return (
    <>
      <PageTitle breadcrumbs={batchesBreadCrumbs}>{t('MENU.FINANCIAL_ACCOUNT')}</PageTitle>

      <FinancialAccountFilterSection
        relatedEntityType={relatedEntityType}
        setRelatedEntityType={setRelatedEntityType}
      />

      <Table
        name={TablesNames.financialAccountTableData}
        data={financialAccountData?.rows ?? []}
        totalItemCount={financialAccountData?.totalItemCount}
        isLoading={isFinancialAccountDataLoading || isPartnerNamesListLoading}
        queryParamsListener={listener}
        // rowActions={rowActions}
        // actionColumnsSize={75}
      />

      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />

      <ModalWindow
        isOpen={financialAccountModalState?.isOpen}
        modalTitle="Edit financial account"
        content={<FinancialAccountModalForm
          data={financialAccountModalState?.data}
          onClose={closeFinancialAccountModal}
          onConfirm={confirmUpdateFinAcc}
        />}
        onClose={closeFinancialAccountModal}
        backdrop="static"
      />

      <ConfirmPasswordModal
        isLoginVisible={isLoginVisible}
        closeModal={closeLoginModal}
        fetchData={(): Promise<void> => fetchAccountData(filtersFromUrl)}
      />
    </>
  );
};